import { useTheme } from 'styled-components';
import { useMarketsContext } from '../../contexts';
import { useSecurity } from '../../hooks';
import { CustomerQuote, ModeEnum, type Quote } from '../../types';
import { QuoteStatusEnum } from '../../types/Status';
import { resolvePricingModeDisplaySpec } from '../../utils';
import { InlineFormattedNumber } from '../FormattedNumber';
import { Icon, IconName } from '../Icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '../Table';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { RejectionWrapper, TextWrapper } from './styles';

export function QuotesSummary({ quote }: { quote: Quote | CustomerQuote }) {
  const { colorTextWarning } = useTheme();

  const { marketsByName } = useMarketsContext();

  const security = useSecurity(quote.Symbol);
  const { priceCurrency, priceIncrement, toDisplayPrice } = resolvePricingModeDisplaySpec({
    pricingMode: quote.PricingMode,
    security,
  });

  const showSizeCounteringQuantities = quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const pricingColumnWidth = showSizeCounteringQuantities ? '18%' : undefined; // looks off when there are 4 columns with different widths in this case, so make it explicit

  const renderPrice = (price: string | null | undefined) => {
    return price == null ? (
      '-'
    ) : (
      <InlineFormattedNumber
        highlightAll={false}
        number={toDisplayPrice(price)}
        currency={priceCurrency}
        increment={priceIncrement}
        pretty={true}
        round={true}
      />
    );
  };

  const renderQuantity = (quantity: string | null | undefined) => {
    return quantity == null ? (
      '-'
    ) : (
      <InlineFormattedNumber
        highlightAll={false}
        number={quantity}
        currency={quote.Currency}
        pretty={true}
        round={true}
      />
    );
  };

  const rejection = () => {
    return (
      <RejectionWrapper>
        <Icon icon={IconName.ExclamationCircle} color={colorTextWarning} />
        <TextWrapper>
          <Text>Rejected: {quote.Text}</Text>
        </TextWrapper>
      </RejectionWrapper>
    );
  };

  if (quote instanceof CustomerQuote) {
    if (quote.QuoteStatus === QuoteStatusEnum.Rejected) {
      return rejection();
    }

    return (
      <Table w="100%">
        <Thead>
          <Tr>
            <Th>Market</Th>
            {showSizeCounteringQuantities && (
              <Th align="right" width={pricingColumnWidth}>
                Bid Qty
              </Th>
            )}
            <Th align="right" width={pricingColumnWidth}>
              Bid
            </Th>
            <Th align="right" width={pricingColumnWidth}>
              Offer
            </Th>
            {showSizeCounteringQuantities && (
              <Th align="right" width={pricingColumnWidth}>
                Offer Qty
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Th>Dealer</Th>
            {showSizeCounteringQuantities && <Td align="right">{renderQuantity(quote.BidQty)}</Td>}
            <Td align="right">{renderPrice(quote.BidPx)}</Td>
            <Td align="right">{renderPrice(quote.OfferPx)}</Td>
            {showSizeCounteringQuantities && <Td align="right">{renderQuantity(quote.OfferQty)}</Td>}
          </Tr>
        </Tbody>
      </Table>
    );
  }

  return quote.Markets == null ? null : (
    <Table w="100%">
      <Thead>
        <Tr>
          <Th>Market</Th>
          {showSizeCounteringQuantities && (
            <Th align="right" width={pricingColumnWidth}>
              Bid Qty
            </Th>
          )}
          <Th align="right" width={pricingColumnWidth}>
            Bid
          </Th>
          <Th align="right" width={pricingColumnWidth}>
            Offer
          </Th>
          {showSizeCounteringQuantities && (
            <Th align="right" width={pricingColumnWidth}>
              Offer Qty
            </Th>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {quote.Markets.map(market => {
          const displayName = marketsByName.get(market.Market)?.DisplayName;

          return (
            <Tr key={`${market.Market}-${market.MarketAccount}`}>
              <Th>
                <Tooltip tooltip={market.Text} usePortal={true}>
                  {displayName}{' '}
                  {market.Text != null && <Icon icon={IconName.ExclamationCircle} variant="Warning" size={10} />}
                </Tooltip>
              </Th>
              {market.Text == null ? (
                <>
                  {showSizeCounteringQuantities && <Td align="right">{renderQuantity(market.BidQty)}</Td>}
                  <Td align="right">{renderPrice(market.BidPx)}</Td>
                  <Td align="right">{renderPrice(market.OfferPx)}</Td>
                  {showSizeCounteringQuantities && <Td align="right">{renderQuantity(market.OfferQty)}</Td>}
                </>
              ) : (
                <Td
                  colSpan={showSizeCounteringQuantities ? 4 : 2}
                  style={{
                    textAlign: 'right',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: 200,
                  }}
                >
                  <div>
                    <Tooltip tooltip={market.Text} usePortal={true}>
                      <div style={{ opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>{market.Text}</div>
                    </Tooltip>
                  </div>
                </Td>
              )}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
